import { useState } from "react";
import api from "@/lib/api";
import { ScanSearch, RefreshCw } from "lucide-react";

const StatusChip = ({ label, clean, testid }) => (
  <span className={`text-[10px] font-bold px-2 py-0.5 border rounded-sm font-mono-data ${clean ? "text-emerald-400 border-emerald-800" : "text-red-400 border-red-800"}`}
    data-testid={testid}>
    {label}: {clean ? "CLEAN" : "HITS FOUND"}
  </span>
);

export const PiiAuditCard = () => {
  const [needle, setNeedle] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [showNotes, setShowNotes] = useState(false);

  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const q = needle.trim().length >= 3 ? `?needle=${encodeURIComponent(needle.trim())}` : "";
      const { data } = await api.get(`/admin/import-pii-audit${q}`);
      setResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Audit failed — check backend logs.");
    } finally { setBusy(false); }
  };

  return (
    <div className="border rounded-sm" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
      data-testid="pii-audit-card">
      <div className="p-4 border-b flex flex-wrap items-center gap-2" style={{ borderColor: "var(--hoip-border)" }}>
        <ScanSearch className="w-4 h-4 text-emerald-500" />
        <span className="text-[10px] tracking-widest opacity-60 font-bold">IMPORT PII AUDIT (FULL-PATH)</span>
        <div className="flex items-center gap-2 ml-auto">
          <input value={needle} onChange={(e) => setNeedle(e.target.value)} placeholder="Optional: scan for a name…"
            className="h-7 px-2 text-[11px] w-52 rounded-sm border bg-[#0A0A0A] border-[#27272A] text-[#F4F4F5]"
            data-testid="pii-audit-needle-input" />
          <button onClick={run} disabled={busy}
            className="flex items-center gap-1 px-3 h-7 text-[10px] font-bold rounded-sm bg-white text-black hover:bg-gray-200 disabled:opacity-50"
            data-testid="pii-audit-run-btn">
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {busy ? "Scanning…" : "Run audit"}
          </button>
        </div>
      </div>
      <div className="p-4 space-y-3 text-[11px]">
        {err && <div className="text-red-400" data-testid="pii-audit-error">{err}</div>}
        {!result && !err && (
          <p style={{ color: "var(--hoip-text-tertiary)" }}>
            Scans every user-data collection and the backend log files for broker account-number patterns,
            raw filename traces, and (optionally) a specific name. Run after any import to verify zero PII persisted.
          </p>
        )}
        {result && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusChip label="DB SCAN" clean={result.db_scan.clean} testid="pii-audit-db-status" />
              <StatusChip label="LOG SCAN" clean={result.log_scan.clean} testid="pii-audit-log-status" />
              <span className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
                {Object.values(result.db_scan.collections_scanned).reduce((a, b) => a + b, 0)} docs across{" "}
                {Object.keys(result.db_scan.collections_scanned).length} collections · {result.log_scan.files_scanned.length} log files ·{" "}
                {new Date(result.generated_at).toLocaleString()}
              </span>
            </div>
            <div>
              <div className="text-[10px] tracking-widest opacity-60 mb-1">UPLOADERS</div>
              {result.uploaders.length === 0 ? (
                <p style={{ color: "var(--hoip-text-tertiary)" }}>No imported trades yet.</p>
              ) : (
                <table className="w-full dense-table" data-testid="pii-audit-uploaders-table">
                  <thead><tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                    <th className="text-left text-[10px] opacity-60">User</th>
                    <th className="text-right text-[10px] opacity-60">Trades</th>
                    <th className="text-right text-[10px] opacity-60">First import</th>
                    <th className="text-right text-[10px] opacity-60">Last import</th>
                    <th className="text-right text-[10px] opacity-60">Stored filenames</th>
                  </tr></thead>
                  <tbody>
                    {result.uploaders.map((u, i) => (
                      <tr key={i} className="border-b" style={{ borderColor: "var(--hoip-divider)" }}>
                        <td className="text-[11px] font-mono-data">{u.email}</td>
                        <td className="text-right font-mono-data">{u.trades}</td>
                        <td className="text-right font-mono-data text-[10px]">{(u.first_import || "").slice(0, 10) || "—"}</td>
                        <td className="text-right font-mono-data text-[10px]">{(u.last_import || "").slice(0, 10) || "—"}</td>
                        <td className={`text-right font-mono-data ${u.stored_filenames.length ? "text-red-400" : "text-emerald-500"}`}>
                          {u.stored_filenames.length === 0 ? "none ✓" : u.stored_filenames.join(", ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {(result.db_scan.hits.length > 0 || result.log_scan.hits.length > 0) && (
              <div data-testid="pii-audit-hits">
                <div className="text-[10px] tracking-widest text-red-400 mb-1">HITS — INVESTIGATE</div>
                <ul className="space-y-0.5 font-mono-data text-[10px] text-red-400">
                  {result.db_scan.hits.map((h, i) => (
                    <li key={`d${i}`}>DB · {h.collection} · {h.pattern} · {h.match_redacted} · {JSON.stringify(h.doc_hint)}</li>
                  ))}
                  {result.log_scan.hits.map((h, i) => (
                    <li key={`l${i}`}>LOG · {h.file}:{h.line_no} · {h.snippet_redacted}</li>
                  ))}
                </ul>
              </div>
            )}
            <button onClick={() => setShowNotes(v => !v)} className="text-[10px] underline"
              style={{ color: "var(--hoip-text-tertiary)" }} data-testid="pii-audit-notes-toggle">
              {showNotes ? "Hide" : "Show"} pipeline notes ({result.pipeline_notes.length})
            </button>
            {showNotes && (
              <ul className="space-y-1 text-[10px] list-disc pl-4" style={{ color: "var(--hoip-text-secondary)" }}
                data-testid="pii-audit-notes">
                {result.pipeline_notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};
